/**
 * The Job — the single record the CRM pipeline, the survey, the design, the
 * documents and the compliance deadlines all hang off.
 *
 * Architecture decision 8: there is no separate "CRM deal" and "design job" to
 * keep in sync. A stage change is a transition on this one record.
 */

export type JobStage =
  | "lead"
  | "qualified"
  | "estimate-sent"
  | "survey-booked"
  | "surveyed"
  | "designed"
  | "proposal-sent"
  | "signed"
  | "scheduled"
  | "installed"
  | "commissioned"
  | "certified"
  | "bus-redeemed"
  | "handover-complete"
  | "lost"
  | "cancelled";

/**
 * Permitted transitions. Enforced in the API so the pipeline cannot reach an
 * impossible state (e.g. a certificate raised before commissioning).
 */
export const JOB_STAGE_TRANSITIONS: Record<JobStage, readonly JobStage[]> = {
  lead: ["qualified", "lost"],
  qualified: ["estimate-sent", "survey-booked", "lost"],
  "estimate-sent": ["survey-booked", "qualified", "lost"],
  "survey-booked": ["surveyed", "qualified", "cancelled"],
  surveyed: ["designed", "survey-booked", "cancelled"],
  designed: ["proposal-sent", "surveyed", "cancelled"],
  "proposal-sent": ["signed", "designed", "lost"],
  signed: ["scheduled", "cancelled"],
  scheduled: ["installed", "cancelled"],
  installed: ["commissioned", "cancelled"],
  commissioned: ["certified"],
  certified: ["bus-redeemed", "handover-complete"],
  "bus-redeemed": ["handover-complete"],
  "handover-complete": [],
  lost: ["qualified"],
  cancelled: ["qualified"],
};

export function canTransition(from: JobStage, to: JobStage): boolean {
  return JOB_STAGE_TRANSITIONS[from].includes(to);
}

/**
 * Every stage, as a runtime array for validating input at the API boundary.
 * Derived from the transition table rather than listed again, so a new stage
 * cannot be accepted by the API without also declaring where it can go.
 */
export const JOB_STAGES = Object.keys(JOB_STAGE_TRANSITIONS) as readonly JobStage[];

export type TaskPriority = "normal" | "high" | "urgent";

export const TASK_PRIORITY_ORDER: Record<TaskPriority, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
};

/** Priorities as a runtime array, ordered most urgent first. */
export const TASK_PRIORITIES = (
  Object.keys(TASK_PRIORITY_ORDER) as TaskPriority[]
).sort((a, b) => TASK_PRIORITY_ORDER[a] - TASK_PRIORITY_ORDER[b]) as readonly TaskPriority[];

export interface Task {
  readonly id: string;
  readonly organisationId: string;
  readonly jobId?: string;
  readonly title: string;
  readonly priority: TaskPriority;
  readonly assignedToUserId?: string;
  readonly dueAt?: string;
  readonly completedAt?: string;
  readonly createdAt: string;
}

/**
 * Compliance deadlines that run as timers on the job.
 *
 * These are the dates that get an installer in trouble, so they are modelled
 * explicitly rather than left to someone's memory:
 *  - mcs-certificate: within 30 calendar days of commissioning (IOR §4.10).
 *  - dno-notification: connect-and-notify within 28 days of installation.
 *  - bus-voucher-expiry: the TRUE BUS redemption deadline — the voucher's own
 *    validity period, 3 months (6 for GSHP/WSHP) from its ISSUE date (Ofgem
 *    Installer Guidance V5.1, Table 8; §5.71, §5.80). See
 *    `busVoucherExpiryDueDate` below: it cannot be started as a timer yet
 *    because the job model does not capture a voucher-issue date or a
 *    technology — that wiring is a documented follow-up.
 *  - bus-voucher-application: within 120 days of the commissioning date on the
 *    MCS certificate, and only when commissioning precedes the application
 *    (V5.1 Table A / §3.13 / §5.74 / §7.10). This is the Stage 1 APPLICATION
 *    window, not redemption. See `busVoucherApplicationDueDate` below.
 *  - bus-redemption: DEPRECATED. This name and its arithmetic (commissioning
 *    date + 120 days) were confirmed against V5.1 to be a mislabelling of the
 *    Stage 1 voucher-application window above, not a redemption deadline —
 *    the true redemption deadline is `bus-voucher-expiry`. Kept as a member
 *    only because existing `deadline_timers` rows and the `deadline_kind`
 *    Postgres enum already carry it (an enum value cannot be dropped or
 *    renamed in place); nothing should start a new timer under this kind.
 *    See `packages/bus/src/deadlines.ts` for the equivalent fix already
 *    landed in that package, and its file header for the full citation trail.
 */
export type DeadlineKind =
  | "mcs-certificate"
  | "dno-notification"
  | "bus-voucher-expiry"
  | "bus-voucher-application"
  | "bus-redemption"
  | "proposal-chase";

export interface DeadlineTimer {
  readonly id: string;
  readonly organisationId: string;
  readonly jobId: string;
  readonly kind: DeadlineKind;
  readonly dueAt: string;
  readonly satisfiedAt?: string;
  readonly escalated: boolean;
}

/** Calendar-day deadline arithmetic, kept pure so it is testable. */
export function addCalendarDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Invalid ISO date: ${isoDate}`);
  }
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Calendar-month arithmetic, clamped to the end of the target month.
 *
 * "Three months from 30 November" is 28 February (29 in a leap year), not
 * 2 March — the corresponding-date rule, and the conservative reading: it
 * never tells an installer a voucher lives a day or two longer than it does.
 * Left to `setUTCMonth` alone JavaScript overflows into the following month
 * and produces the generous answer instead. Matches
 * `addCalendarMonths` in `@sts/bus`'s `deadlines.ts`.
 */
export function addCalendarMonths(isoDate: string, months: number): string {
  const d = new Date(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Invalid ISO date: ${isoDate}`);
  }
  const dayOfMonth = d.getUTCDate();

  // Move to the 1st first, so the month shift cannot overflow on the way.
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);

  // Day 0 of the following month is the last day of this one.
  const lastDayOfTargetMonth = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0),
  ).getUTCDate();

  d.setUTCDate(Math.min(dayOfMonth, lastDayOfTargetMonth));
  return d.toISOString().slice(0, 10);
}

/** IOR §4.10 — the MCS certificate must be raised within 30 calendar days. */
export function mcsCertificateDueDate(commissioningDate: string): string {
  return addCalendarDays(commissioningDate, 30);
}

/**
 * BUS Stage 1 — the voucher APPLICATION must be made within 120 days of the
 * commissioning date on the MCS certificate (Ofgem Installer Guidance V5.1,
 * Table A "Timing", §3.13, §5.74, §7.10), and only when commissioning happens
 * before the application (§3.13: "If commissioning occurs before the voucher
 * application..."). This is NOT a redemption deadline — see `DeadlineKind`'s
 * doc comment and `busVoucherExpiryDueDate` below for the deadline that is.
 *
 * Formerly exported as `busRedemptionDueDate`, under the `"bus-redemption"`
 * kind: the arithmetic (commissioning + 120 days) is unchanged, only the name
 * was wrong. See `packages/bus/src/deadlines.ts`'s `voucherApplicationDeadline`
 * for the equivalent, already-fixed function in that package, which this
 * arithmetic is pinned to match.
 */
export function busVoucherApplicationDueDate(commissioningDate: string): string {
  return addCalendarDays(commissioningDate, 120);
}

/**
 * BUS Stage 2 — the TRUE redemption deadline: a voucher expires a fixed
 * number of calendar months after its ISSUE date, not after commissioning
 * (Ofgem Installer Guidance V5.1, Table 8; §5.71 "A BUS voucher must be
 * redeemed within its validity period..."; §5.80). `validityMonths` is 3 for
 * air source (AWHP/AAHP) and biomass, 6 for ground and water source
 * (GSHP/WSHP) — the caller supplies it because this module has no BUS
 * technology of its own to resolve it from (see `@sts/bus`'s
 * `voucherValidityMonths`, which reads the figure from a ruleset rather than
 * hard-coding it, and which this arithmetic is pinned to match).
 *
 * NOT WIRED UP: nothing in `apps/api` calls this yet, and it cannot be,
 * because a job currently has no voucher-issue date and no technology to look
 * up — `transitionJobStage` only ever records a commissioning date. Starting
 * a `"bus-voucher-expiry"` timer anchored to anything else (commissioning
 * date included) would reproduce the exact bug this function exists to fix
 * with a new label. Wiring this in is a documented follow-up that needs the
 * job model extended to capture both facts first.
 */
export function busVoucherExpiryDueDate(
  voucherIssueDate: string,
  validityMonths: number,
): string {
  return addCalendarMonths(voucherIssueDate, validityMonths);
}

/** ENA connect-and-notify — within 28 days for a new maximum demand of 60 A or less. */
export function dnoNotificationDueDate(installationDate: string): string {
  return addCalendarDays(installationDate, 28);
}
